import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useUserDetail } from '../../queries/users';
import { UserActivityTab } from './UserActivityTab';
import { UserPaymentsTab } from './UserPaymentsTab';
import { SubscriptionsTab } from './SubscriptionsTab';
import { UserMessagesTab } from './UserMessagesTab';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/utils';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'payments', label: 'Payments' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'messages', label: 'Messages' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export function UserDetailPage() {
  const { userId = '' } = useParams<{ userId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: TabKey = TABS.some((t) => t.key === searchParams.get('tab'))
    ? (searchParams.get('tab') as TabKey)
    : 'overview';

  const { data: user, isLoading, error } = useUserDetail(userId);

  if (isLoading) {
    return <Spinner label="Loading user…" />;
  }

  if (error || !user) {
    return (
      <div className="py-8 text-center text-destructive">
        {error instanceof Error ? error.message : 'User not found.'}
      </div>
    );
  }

  const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || `User ${user.id}`;

  return (
    <div className="space-y-6">
      <Button asChild variant="outline" size="sm" className="self-start">
        <Link to="/users">
          <ChevronLeft className="h-4 w-4" />
          Back to Users
        </Link>
      </Button>

      {/* Identity is always visible regardless of tab, not buried behind a
          click - old admin required opening the Activity tab first to confirm
          which user was even being viewed. */}
      <Card className="p-4 sm:p-6">
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{displayName}</h1>
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>{user.email ?? 'No email on file'}</span>
          {user.phone_number && <span>{user.phone_number}</span>}
          <span>User ID: {user.id}</span>
        </div>
      </Card>

      <div className="flex items-center gap-1 overflow-x-auto border-b border-border">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setSearchParams(tab.key === 'overview' ? {} : { tab: tab.key })}
            className={cn(
              '-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              activeTab === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <UserActivityTab user={user} />}
      {activeTab === 'payments' && <UserPaymentsTab userId={userId} />}
      {activeTab === 'subscriptions' && <SubscriptionsTab userId={userId} />}
      {activeTab === 'messages' && <UserMessagesTab userId={user.id} />}
    </div>
  );
}
